import { zodResolver } from "@hookform/resolvers/zod";
import { Add, Delete } from "@mui/icons-material";
import { Box, Button, Card, FormControl, FormLabel, IconButton, Stack, Typography } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { ApiChallenge } from "griptonite-api-client";
import { useEffect, useMemo } from "react";
import { FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";

import { useChallengeQuery, useUpdateChallengeMutation } from "@/hooks/challenges";
import { useCurrentVenueQuery } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import CheckboxInput from "../forms/CheckboxInput";
import { MediaSelectInput } from "../forms/MediaInput";
import RadioGroupInput from "../forms/RadioGroupInput";
import { NumberInput } from "../forms/TextInput";
import TextInput, { TextareaInput } from "../forms/TextInput";
import BottomButtons from "./BottomButtons";
import {
  ChallengeRulesFormSchema,
  ChallengeRulesFormValues,
  challengeRulesFormValuesToPayload,
  challengeToRulesFormValues,
} from "./forms";
import { useUnhidePreviousTabWarningsOnMount } from "./getPublishWarnings";

const ChallengeRulesForm = ({ challengeUuid }: { challengeUuid: string }) => {
  const navigate = useNavigate();
  const { data: venue } = useCurrentVenueQuery();

  const { data: challenge, isPending } = useChallengeQuery(challengeUuid, venue?.uuid);

  const defaultValues = useMemo(() => challengeToRulesFormValues(challenge), [challenge]);

  const methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(ChallengeRulesFormSchema),
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const updateMutation = useUpdateChallengeMutation(challenge?.uuid, venue?.uuid);

  // Unhide warnings from the previous tab when this component mounts
  useUnhidePreviousTabWarningsOnMount(challengeUuid, "details");

  if (isPending) {
    return null;
  }

  const onSubmit = async (values: ChallengeRulesFormValues) => {
    const payload = challengeRulesFormValuesToPayload(values);
    const challenge = await updateMutation.mutateAsync(payload, {
      onError: async (error) => {
        handleFormError(error, methods);
      },
    });
    if (challenge.isHidden) {
      navigate({
        to: "/gym/$venueUuid/challenges/$challengeUuid/profile/promotion",
        from: "/gym/$venueUuid/challenges/$challengeUuid/profile/rules",
        replace: true,
      });
    }
  };
  return (
    <Stack
      sx={{ flex: 1, overflowY: "auto", minHeight: 0, padding: 2 }}
      component="form"
      autoComplete="off"
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <FormProvider {...methods}>
        <Box sx={{ flex: 1 }}>
          <RulesFields challenge={challenge} />
        </Box>
        <Box>
          <BottomButtons
            challenge={challenge}
            formState={methods.formState}
            prevLink={{
              to: "/gym/$venueUuid/challenges/$challengeUuid/profile/details",
              from: "/gym/$venueUuid/challenges/$challengeUuid/profile/rules",
            }}
            nextLink={{
              to: "/gym/$venueUuid/challenges/$challengeUuid/profile/promotion",
              from: "/gym/$venueUuid/challenges/$challengeUuid/profile/rules",
            }}
          />
        </Box>
      </FormProvider>
    </Stack>
  );
};

const RulesFields = ({ challenge }: { challenge?: ApiChallenge }) => {
  const { data: currentVenue } = useCurrentVenueQuery();
  return (
    <>
      <Stack
        spacing={2}
        padding={2}
        marginX={-2}
        marginTop={-2}
        sx={{
          borderBottom: "1px solid",
          borderColor: "neutral.200",
        }}
      >
        <Typography level="h4">Rules</Typography>
        <Box>
          <NumberInput
            name="minimumRoutesPerSession"
            label="Minimum climbs per session"
            min={1}
            disabled={!!challenge?.entriesCount}
          />
          <Typography level="body-sm" sx={{ color: "neutral.600" }} marginTop={1}>
            Users need to scan one tag to prove location and climb this many climbs to qualify a
            successful check-in.
          </Typography>
        </Box>

        {(challenge?.venues.length || 0) > 1 || (currentVenue?.climbTypes.length || 0) > 1 ? (
          <FormControl>
            <FormLabel>Disciplines</FormLabel>
            <Stack direction="row" spacing={2}>
              <CheckboxInput name="allowSport" label="Sport" disabled={!!challenge?.entriesCount} />
              <CheckboxInput
                name="allowBouldering"
                label="Bouldering"
                disabled={!!challenge?.entriesCount}
              />
            </Stack>
          </FormControl>
        ) : null}

        <Box>
          <RadioGroupInput
            name="allowReentry"
            label="Re-entry"
            options={[
              {
                value: true,
                label: "Allowed",
                helpText:
                  "Allow re-entry after completing the challenge. Unclaimed prizes will be lost after re-entry.",
              },
              {
                value: false,
                label: "Not allowed",
                helpText:
                  "Once a user has completed the challenge, they are not allowed to enter again.",
              },
            ]}
          />
          <Typography level="body-sm" sx={{ mt: 1, color: "neutral.600" }}></Typography>
        </Box>
      </Stack>

      <Box sx={{ padding: 2 }} marginX={-2}>
        <Typography level="h4">Tiers & rewards</Typography>
        <Typography level="body-sm" sx={{ mb: 2 }}>
          Customers win prizes at Tiered check points. Specify the prize and number of check-ins
          required at each tier below. You can have up to three tiers.
        </Typography>

        <TierFields challenge={challenge} />

        <Box mt={3}>
          <RadioGroupInput
            name="lockMode"
            label="Tier reward visibility"
            options={[
              {
                value: "NONE",
                label: "Always visible",
                helpText: "All tier rewards are always visible",
              },
              {
                value: "UNTIL_PREV_COMPLETED",
                label: "Progressive",
                helpText: "To see a tier's rewards, users must complete the previous tier",
              },
              {
                value: "UNTIL_COMPLETED",
                label: "Surprise",
                helpText:
                  "Users only find out what their rewards are when they reach the tier goal",
              },
            ]}
          />
        </Box>
      </Box>
    </>
  );
};

const TierFields = ({ challenge }: { challenge?: ApiChallenge }) => {
  const { control } = useFormContext<ChallengeRulesFormValues>();

  const {
    fields: tiersFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "tiers",
  });

  const addTier = () => {
    append({
      targetValue: null,
      rewards: [],
    });
  };

  const canEditOrDeleteExistingTiers = !challenge?.entriesCount;

  return (
    <Box>
      <Stack spacing={2} flexWrap="wrap" direction="row" alignItems="stretch">
        {tiersFields.map((tier, tierIndex) => {
          const editDeleteDisabled = !canEditOrDeleteExistingTiers && !!tier.uuid;
          return (
            <Card key={tier.id} variant="outlined" sx={{ p: 2, minWidth: 200, flex: 1 }}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography level="title-md">Tier {tierIndex + 1}</Typography>
                  {tierIndex > 0 && !editDeleteDisabled ? (
                    <IconButton
                      variant="soft"
                      color="danger"
                      size="sm"
                      onClick={() => remove(tierIndex)}
                    >
                      <Delete />
                    </IconButton>
                  ) : null}
                </Stack>

                <NumberInput
                  name={`tiers.${tierIndex}.targetValue`}
                  label="Check-in goal"
                  min={1}
                  placeholder="e.g. 5"
                  disabled={editDeleteDisabled}
                />

                <TextInput
                  name={`tiers.${tierIndex}.rewards.0.name`}
                  label="Reward name"
                  placeholder="e.g. Free coffee"
                />
                <NumberInput
                  name={`tiers.${tierIndex}.rewards.0.quantityAvailable`}
                  label="Reward quantity available"
                  placeholder="(unlimited)"
                />

                <TextareaInput
                  name={`tiers.${tierIndex}.rewards.0.description`}
                  label="Reward description"
                  minRows={2}
                />

                <MediaSelectInput
                  name={`tiers.${tierIndex}.rewards.0.image`}
                  label="Reward image"
                  accept="image/*"
                  multiple={false}
                />
              </Stack>
            </Card>
          );
        })}

        {tiersFields.length < 3 && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 2,
              whiteSpace: "nowrap",
            }}
          >
            <Button variant="outlined" startDecorator={<Add />} onClick={addTier}>
              Add tier {tiersFields.length + 1}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ChallengeRulesForm;
